/**
 * Color utility functions for intelligent contrast detection and branding
 */

// Calculate relative luminance using W3C formula
export function getLuminance(hexColor: string): number {
  // Remove # if present
  const hex = hexColor.replace('#', '')
  
  // Parse hex to RGB
  const rgb = parseInt(hex, 16)
  const r = (rgb >> 16) & 255
  const g = (rgb >> 8) & 255
  const b = rgb & 255

  // Calculate relative luminance using W3C formula
  // Formula: (0.299 * R + 0.587 * G + 0.114 * B) / 255
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance
}

// Determine if a color is dark (luminance < 0.5)
export function isDarkColor(hexColor: string): boolean {
  return getLuminance(hexColor) < 0.5
}

// Lighten a color by a percentage (percent = 0-1, where 1 = 100% lighter)
export function lightenColor(hexColor: string, percent: number = 0.9): string {
  const hex = hexColor.replace('#', '')
  const rgb = parseInt(hex, 16)
  
  const r = (rgb >> 16) & 255
  const g = (rgb >> 8) & 255
  const b = rgb & 255

  // Lighten by interpolating towards white (255, 255, 255)
  const newR = Math.round(r + (255 - r) * (1 - percent))
  const newG = Math.round(g + (255 - g) * (1 - percent))
  const newB = Math.round(b + (255 - b) * (1 - percent))

  const result = ((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, '0')
  return `#${result}`
}

// Get box styling based on header background color (for all regular boxes)
export function getOrgBrandingBoxStyles(headerBackgroundColor: string) {
  const dark = isDarkColor(headerBackgroundColor)
  
  return {
    backgroundColor: dark ? '#ffffff' : lightenColor(headerBackgroundColor, 0.9),
    border: `2px solid ${headerBackgroundColor}`,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: headerBackgroundColor,
  }
}

// Microsoft green - complements any branding color
function getSuccessGreen(): string {
  return '#107C10'
}

// Get box styling for success/completion boxes (Recommended & License Analysis Complete)
export function getSuccessBoxStyles() {
  return {
    backgroundColor: '#f0f9f6', // Very light green background
    border: `2px solid ${getSuccessGreen()}`, // Dark green accent
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: getSuccessGreen(),
  }
}
